import { useState, useRef, useEffect } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { aiAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Sparkles, Send, Loader2, Copy, Clock, Wand2, Lightbulb,
  Hash, PenSquare, Target, Zap, Crown
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '../components/ui/tooltip';

const QUICK_PROMPTS = [
  { icon: PenSquare, label: 'Write Caption', prompt: 'Write an engaging social media caption for ' },
  { icon: Hash, label: 'Hashtags', prompt: 'Generate 15 relevant hashtags for ' },
  { icon: Target, label: 'Content Strategy', prompt: 'Create a 7-day content strategy for ' },
  { icon: Lightbulb, label: 'Post Ideas', prompt: 'Give me 10 creative post ideas for ' },
  { icon: Wand2, label: 'Rewrite', prompt: 'Rewrite this content to be more engaging: ' },
  { icon: Zap, label: 'Ad Copy', prompt: 'Write compelling ad copy for ' },
];

export default function AIAssistantPage() {
  const { currentWorkspace } = useWorkspace();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [provider, setProvider] = useState('openai');
  const [tone, setTone] = useState('professional');
  const [proMode, setProMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (currentWorkspace?.id) {
      aiAPI.listThreads(currentWorkspace.id)
        .then(res => setHistory(res.data))
        .catch(err => console.error(err));
    }
  }, [currentWorkspace?.id]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const PRO_MODELS = {
    openai: 'gpt-4.1',
    anthropic: 'claude-4-opus-20250514',
    gemini: 'gemini-2.5-pro',
  };
  const STANDARD_MODELS = {
    openai: 'gpt-4.1-mini',
    anthropic: 'claude-4-sonnet-20250514',
    gemini: 'gemini-2.5-flash',
  };

  const handleSend = async (promptOverride = null) => {
    const text = promptOverride || input;
    if (!text.trim()) return;
    
    const selectedModel = proMode ? PRO_MODELS[provider] : STANDARD_MODELS[provider];
    const userMsg = { role: 'user', text, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await aiAPI.generate({
        workspace_id: currentWorkspace.id,
        prompt: text,
        provider,
        tone,
        model: selectedModel,
      });
      const aiMsg = {
        role: 'assistant',
        text: res.data.text,
        provider: res.data.provider,
        model: res.data.model,
        latency: res.data.latency_ms,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, aiMsg]);
    } catch (err) {
      const errorMsg = {
        role: 'error',
        text: err.response?.data?.detail || 'AI generation failed',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="p-6 h-[calc(100vh-3.5rem)] flex flex-col animate-fade-in-up">
      <div className="mb-4">
        <h1 className="text-2xl font-display font-semibold">AI Assistant</h1>
        <p className="text-sm text-muted-foreground mt-1">Generate content, strategies, and ideas with AI.</p>
      </div>

      <div className="flex-1 flex gap-6 min-h-0">
        {/* Chat Panel */}
        <div className="flex-1 flex flex-col">
          {/* Controls */}
          <div className="flex gap-2 mb-3 items-center flex-wrap">
            <Tabs value={provider} onValueChange={setProvider}>
              <TabsList className="h-8">
                <TabsTrigger value="openai" className="text-xs h-7 px-3">OpenAI</TabsTrigger>
                <TabsTrigger value="anthropic" className="text-xs h-7 px-3">Claude</TabsTrigger>
                <TabsTrigger value="gemini" className="text-xs h-7 px-3">Gemini</TabsTrigger>
              </TabsList>
            </Tabs>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger className="h-8 w-[130px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="professional">Professional</SelectItem>
                <SelectItem value="casual">Casual</SelectItem>
                <SelectItem value="humorous">Humorous</SelectItem>
                <SelectItem value="formal">Formal</SelectItem>
                <SelectItem value="bold">Bold & Edgy</SelectItem>
              </SelectContent>
            </Select>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2 ml-auto px-3 py-1.5 rounded-lg bg-secondary/60 border border-border">
                    <Crown className={`h-3.5 w-3.5 transition-colors ${proMode ? 'text-amber-400' : 'text-muted-foreground'}`} />
                    <Label htmlFor="pro-mode" className="text-xs font-medium cursor-pointer select-none">
                      Pro
                    </Label>
                    <Switch
                      id="pro-mode"
                      checked={proMode}
                      onCheckedChange={setProMode}
                      className="scale-75"
                      data-testid="pro-mode-toggle"
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[220px]">
                  <p className="text-xs">
                    {proMode ? (
                      <><strong>Pro Mode ON</strong> — Using {PRO_MODELS[provider]}. Higher quality, uses more credits.</>
                    ) : (
                      <><strong>Standard Mode</strong> — Using {STANDARD_MODELS[provider]}. Fast and affordable.</>
                    )}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* Messages */}
          <Card className="flex-1 bg-card/60 backdrop-blur flex flex-col min-h-0">
            <ScrollArea className="flex-1 p-4" ref={scrollRef}>
              {messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center py-12">
                  <Sparkles className="h-12 w-12 text-primary/40 mb-4" />
                  <p className="text-lg font-medium text-foreground/80">How can I help you today?</p>
                  <p className="text-sm text-muted-foreground mt-1">Choose a quick prompt or type your own.</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-6 max-w-lg">
                    {QUICK_PROMPTS.map((qp, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setInput(qp.prompt);
                          inputRef.current?.focus();
                        }}
                        className="flex items-center gap-2 p-3 rounded-lg border bg-secondary/30 hover:bg-secondary/60 transition-colors text-left"
                      >
                        <qp.icon className="h-4 w-4 text-primary shrink-0" />
                        <span className="text-xs">{qp.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-xl p-3 ${
                        msg.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : msg.role === 'error'
                            ? 'bg-destructive/10 text-destructive border border-destructive/20'
                            : 'bg-secondary'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                        {msg.role === 'assistant' && (
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/30">
                            <p className="text-[10px] text-muted-foreground">
                              {msg.provider}/{msg.model} &middot; {msg.latency}ms
                            </p>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => copyToClipboard(msg.text)}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {loading && (
                    <div className="flex justify-start">
                      <div className="bg-secondary rounded-xl p-3">
                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>

            {/* Input */}
            <div className="p-3 border-t">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="Ask the AI assistant..."
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
                  disabled={loading}
                  data-testid="ai-assistant-input"
                />
                <Button onClick={() => handleSend()} disabled={loading || !input.trim()} data-testid="ai-assistant-send-button">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* History Sidebar */}
        <div className="hidden xl:block w-[300px]">
          <Card className="bg-card/60 backdrop-blur h-full">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Clock className="h-4 w-4" /> Recent Generations
              </CardTitle>
            </CardHeader>
            <ScrollArea className="h-[calc(100%-56px)]">
              <CardContent className="space-y-2">
                {history.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-4">No history yet</p>
                ) : (
                  history.slice(0, 20).map(thread => (
                    <div
                      key={thread.id}
                      className="p-2 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors cursor-pointer"
                      onClick={() => {
                        setMessages(prev => [
                          ...prev,
                          { role: 'user', text: thread.prompt, timestamp: thread.created_at },
                          { role: 'assistant', text: thread.response, provider: thread.provider, model: thread.model, latency: thread.latency_ms, timestamp: thread.created_at }
                        ]);
                      }}
                    >
                      <p className="text-xs font-medium truncate">{thread.prompt}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className="text-[10px] bg-secondary">{thread.provider}</Badge>
                        <span className="text-[10px] text-muted-foreground">{thread.latency_ms}ms</span>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </ScrollArea>
          </Card>
        </div>
      </div>
    </div>
  );
}
